import React, { useMemo } from 'react';

const ContributionHeatmap = ({ commits = [], title = "Contribution Activity" }) => {
  const heatmapData = useMemo(() => {
    // Get commits from last 365 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 364);

    // Initialize all days with 0 commits
    const days = [];
    const contributionMap = {};

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      contributionMap[dateKey] = 0;
    }

    // Count commits per day
    commits.forEach(commit => {
      const commitDate = new Date(commit.commit?.author?.date || commit.committedDate || commit.date);
      const dateKey = commitDate.toISOString().split('T')[0];
      if (contributionMap[dateKey] !== undefined) {
        contributionMap[dateKey]++;
      }
    });

    // Convert to array format
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      days.push({
        date: new Date(d),
        dateKey,
        count: contributionMap[dateKey],
        weekday: d.getDay()
      });
    }

    return days;
  }, [commits]);

  // Group days by week
  const weeks = useMemo(() => {
    const weekGroups = [];
    let currentWeek = [];

    // Pad beginning to start on Sunday
    const firstDay = heatmapData[0];
    if (firstDay && firstDay.weekday !== 0) {
      for (let i = 0; i < firstDay.weekday; i++) {
        currentWeek.push(null);
      }
    }

    heatmapData.forEach((day, index) => {
      currentWeek.push(day);
      
      if (day.weekday === 6 || index === heatmapData.length - 1) {
        weekGroups.push([...currentWeek]);
        currentWeek = [];
      }
    });

    return weekGroups;
  }, [heatmapData]);

  const getColor = (count) => {
    if (count === 0) return 'bg-muted hover:bg-muted/80';
    if (count <= 2) return 'bg-green-200 hover:bg-green-300';
    if (count <= 5) return 'bg-green-400 hover:bg-green-500';
    if (count <= 10) return 'bg-green-600 hover:bg-green-700';
    return 'bg-green-800 hover:bg-green-900';
  };

  const monthLabels = useMemo(() => {
    const labels = [];
    let currentMonth = -1;
    
    weeks.forEach((week, weekIndex) => {
      const firstDay = week.find(d => d !== null);
      if (firstDay) {
        const month = firstDay.date.getMonth();
        if (month !== currentMonth) {
          currentMonth = month;
          labels.push({
            weekIndex,
            label: firstDay.date.toLocaleDateString('en-US', { month: 'short' })
          });
        }
      }
    });
    
    return labels;
  }, [weeks]);

  const totalContributions = heatmapData.reduce((sum, day) => sum + day.count, 0);
  const maxStreak = useMemo(() => {
    let current = 0;
    let max = 0;
    heatmapData.forEach(day => {
      if (day.count > 0) {
        current++;
        max = Math.max(max, current);
      } else {
        current = 0;
      }
    });
    return max;
  }, [heatmapData]);

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold">{title}</h3>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>{totalContributions} contributions</span>
          <span>Longest streak: {maxStreak} days</span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          {/* Month labels */}
          <div className="flex gap-[3px] mb-2 ml-8">
            <div className="relative" style={{ width: `${weeks.length * 13}px` }}>
              {monthLabels.map(({ weekIndex, label }) => (
                <div
                  key={weekIndex}
                  className="absolute text-xs text-muted-foreground"
                  style={{ left: `${weekIndex * 13}px` }}
                >
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap grid */}
          <div className="flex gap-[3px]">
            {/* Day labels */}
            <div className="flex flex-col gap-[3px] text-xs text-muted-foreground pr-2">
              <div style={{ height: '10px' }}></div>
              <div style={{ height: '10px' }}>Mon</div>
              <div style={{ height: '10px' }}></div>
              <div style={{ height: '10px' }}>Wed</div>
              <div style={{ height: '10px' }}></div>
              <div style={{ height: '10px' }}>Fri</div>
              <div style={{ height: '10px' }}></div>
            </div>

            {/* Weeks */}
            {weeks.map((week, weekIndex) => (
              <div key={weekIndex} className="flex flex-col gap-[3px]">
                {week.map((day, dayIndex) => {
                  if (!day) {
                    return <div key={`empty-${dayIndex}`} style={{ width: '10px', height: '10px' }} />;
                  }

                  return (
                    <div
                      key={day.dateKey}
                      className={`w-[10px] h-[10px] rounded-sm ${getColor(day.count)} transition-colors cursor-pointer`}
                      title={`${day.date.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}: ${day.count} contribution${day.count !== 1 ? 's' : ''}`}
                    />
                  );
                })}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-2 mt-4 text-xs text-muted-foreground">
            <span>Less</span>
            <div className="flex gap-1">
              <div className="w-[10px] h-[10px] rounded-sm bg-muted" />
              <div className="w-[10px] h-[10px] rounded-sm bg-green-200" />
              <div className="w-[10px] h-[10px] rounded-sm bg-green-400" />
              <div className="w-[10px] h-[10px] rounded-sm bg-green-600" />
              <div className="w-[10px] h-[10px] rounded-sm bg-green-800" />
            </div>
            <span>More</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContributionHeatmap;
